import {
  ArrowsLeftRightIcon,
  CoinIcon,
  CursorClickIcon,
  MagnifyingGlassIcon,
  PackageIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react'

import { transactionStatuses } from '@/common/constants'
import { getShipmentStatusOptions } from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/helpers'
import { alertLabels as shipmentAlertLabels, normalizedTrackingStatusMessages } from '@/parcels/constants'
import { transactionAlertTexts } from '@/returns/constants'

import DisabledTriggerLabel from './DisabledTriggerLabel/DisabledTriggerLabel'

const buildAlertRaisedSections = () => {
  const ignoredAlertKeys = ['has_alerts']
  const labelOverrides = {
    refund_on_hold: 'Reembolso en espera',
    exchange_on_hold: 'Pedido de cambio en espera',
    exchange_order_error: 'Error al crear pedido de cambio',
    time_issue: 'Problema urgente',
  }

  const buildValue = ([value, label]) => ({ value, label: labelOverrides[value] ?? label })

  const shipmentValues = Object.entries(shipmentAlertLabels)
    .filter(([value]) => !ignoredAlertKeys.includes(value))
    .map(buildValue)

  const shipmentValueKeys = new Set(shipmentValues.map(({ value }) => value))

  const returnValues = Object.entries(transactionAlertTexts)
    .filter(([value]) => !ignoredAlertKeys.includes(value) && !shipmentValueKeys.has(value))
    .map(buildValue)

  return {
    shipment: { label: 'Envíos', values: shipmentValues },
    return: { label: 'Solicitudes de devolución', values: returnValues },
  }
}

const buildTrackingStatusValues = () => {
  const values = []
  Object.values(normalizedTrackingStatusMessages).forEach(statuses => {
    Object.entries(statuses).forEach(([statusKey, statusLabel]) => {
      values.push({ label: statusLabel, value: statusKey })
    })
  })
  return values
}

const daysUnchangedInputOptions = {
  field: 'days_unchanged',
  label: 'Días sin cambios',
  type: 'number',
  validate: value => {
    const numeric = Number(value)

    if (!Number.isInteger(numeric) || numeric < 1) {
      return 'Introduce un número entero mayor o igual a 1'
    }

    return null
  },
}

export const getTriggerEventSections = () => ({
  transaction: {
    label: 'Transacción',
    icon: ArrowsLeftRightIcon,
    events: {
      transaction_status_updated: {
        label: 'Estado de transacción actualizado',
        description:
          'Se activa cada vez que el estado de una transacción cambia a alguno de los estados seleccionados.',
        valueOptions: { values: transactionStatuses, label: 'Valores' },
      },
      transaction_status_unchanged: {
        label: 'Estado de transacción sin cambios',
        description:
          'Se activa si el estado de una transacción no cambia durante el número de días seleccionado en alguno de los estados seleccionados',
        inputOptions: daysUnchangedInputOptions,
        valueOptions: { values: transactionStatuses, label: 'Estado de transacción' },
      },
    },
  },
  shipment: {
    label: 'Envío',
    icon: PackageIcon,
    events: {
      shipment_status_updated: {
        label: 'Estado de envío actualizado',
        description: 'Se activa la primera vez que el estado de un envío cambia a uno de los estados seleccionados.',
        valueOptions: {
          values: getShipmentStatusOptions(),
          label: 'Valores',
        },
      },
      shipment_status_unchanged: {
        label: 'Estado de envío sin cambios',
        description:
          'Se activa si el estado de un envío no cambia durante el número de días seleccionado en alguno de los estados seleccionados',
        inputOptions: daysUnchangedInputOptions,
        valueOptions: { values: getShipmentStatusOptions(), label: 'Estado del envío' },
      },
      shipment_tracking_status_updated: {
        label: 'Seguimiento del envío actualizado',
        description:
          'Se activa la primera vez que el estado de seguimiento de un envío cambia a alguno de los estados seleccionados.',
        valueOptions: { values: buildTrackingStatusValues(), label: 'Valores' },
      },
      shipment_tracking_status_unchanged: {
        label: 'Estado de seguimiento del envío sin cambios',
        description:
          'Se activa si el estado de seguimiento de un envío no cambia durante el número de días seleccionado en alguno de los estados seleccionados',
        inputOptions: daysUnchangedInputOptions,
        valueOptions: { values: buildTrackingStatusValues(), label: 'Estado de seguimiento' },
      },
    },
  },
  refundPayment: {
    label: 'Reembolso / Pago',
    icon: CoinIcon,
    events: {
      refund_issued: {
        label: 'Reembolso emitido',
        description: 'Se activa cuando se emite un reembolso por una transacción.',
        valueOptions: {
          values: [
            { label: 'Primer reembolso', value: 'first_refund' },
            { label: 'Reembolso adicional', value: 'additional_refund' },
          ],
          label: 'Tipo de reembolso',
        },
      },
      payment_required: {
        label: 'Pago requerido',
        description:
          'Se activa cuando se crea una orden de cambio y se requiere completar el pago pendiente para confirmarla y procesarla.',
      },
      payment_request_expired: {
        label: 'Solicitud de pago caducada',
        description: 'Se activa cuando una solicitud de pago caduca automáticamente tras el cierre del plazo de pago.',
      },
    },
  },
  transactionActions: {
    label: 'Acciones de transacción',
    icon: CursorClickIcon,
    events: {
      items_amended: {
        label: 'Corregir productos rechazados',
        description:
          'Se activa cuando los productos devueltos que habían sido rechazados anteriormente se corrigen manualmente desde la página de transacciones.',
      },
      return_customer_info_updated: {
        label: 'Editar datos del cliente',
        description: 'Se activa cuando se actualizan manualmente los datos del cliente para una transacción.',
      },
      return_address_updated: {
        label: 'Editar dirección de devolución/recogida',
        description:
          'Se activa cuando se actualiza manualmente la dirección de devolución o recogida (y se genera una nueva etiqueta de devolución).',
      },
      pickup_scheduled: {
        label: 'Programar nueva recogida',
        description: 'Se activa cuando se programa manualmente una nueva fecha de recogida para la devolución.',
      },
      return_instructions_request: {
        label: 'Reenviar instrucciones de devolución',
        description:
          'Se activa cuando se utiliza la acción de reenviar las instrucciones de devolución (y la etiqueta de devolución, si procede) al cliente.',
      },
      switch_to_refund: {
        label: 'Cambiar cambio por devolución',
        description:
          'Se activa cuando una solicitud de cambio se convierte manualmente en una solicitud de devolución, cancelando así los nuevos productos solicitados y el pedido de cambio asociados.',
      },
      return_uncancelled: {
        label: 'Revertir estado cancelado',
        description: 'Se activa cuando una transacción anulada se reactiva manualmente.',
      },
      mark_as_paid: {
        label: 'Marcar como pagado',
        description: 'Se activa cuando un pago pendiente se marca manualmente como pagado.',
      },
      mark_as_refunded: {
        label: 'Marcar como reembolsado',
        description: 'Se activa cuando un reembolso pendiente se marca manualmente como reembolsado.',
      },
      put_refund_on_hold: {
        label: 'Reembolso puesto en espera',
        description: 'Se activa cuando el reembolso pendiente de una transacción se pone en espera manualmente.',
      },
      on_hold_return_approved: {
        label: 'Solicitud de On Hold aprobada',
        description: 'Se activa cuando se aprueba manualmente una solicitud de devolución que estaba en espera.',
      },
      on_hold_return_declined: {
        label: 'Solicitud de On Hold rechazada',
        description: 'Se activa cuando se rechaza manualmente una solicitud de devolución que estaba en espera.',
      },
      send_payment_link: {
        label: 'Reenviar link de pago',
        description: 'Se activa cuando se utiliza la acción de reenviar el link de pago al cliente.',
      },
      cancel_store_credit: {
        label: 'Anular crédito de tienda',
        description: 'Se activa cuando se anula manualmente el crédito de tienda emitido en una devolución anterior.',
      },
      cancel_gift_card: {
        label: 'Anular tarjeta regalo',
        description: 'Se activa cuando se anula manualmente una tarjeta regalo emitida en un reembolso anterior.',
      },
      allowed_list_created: {
        label: 'Crear excepción',
        description: 'Se activa cuando se crea una excepción en la política de devoluciones para un pedido.',
      },
      return_label_created: {
        label: 'Nueva etiqueta solicitada',
        description: 'Se activa cuando se solicita una nueva etiqueta de devolución para la transacción.',
      },
      delivery_method_updated: {
        label: 'Cambiar método de devolución',
        description: 'Se activa cuando se cambia el método de devolución de una transacción.',
      },
      change_refund_method: {
        label: 'Cambiar método de reembolso',
        description: 'Se activa cuando se cambia el método de reembolso de una transacción.',
      },
    },
  },
  shipmentActions: {
    label: 'Acciones de envío',
    icon: CursorClickIcon,
    events: {
      shipment_items_amended: {
        label: 'Corregir estado de empaquetado',
        description:
          'Se activa cuando se revierte manualmente el estado de empaquetado de productos que se habían incluido en el envío.',
      },
      shipment_split: {
        label: 'Partir envío',
        description: 'Se activa cuando los productos de un envío se dividen manualmente en varios envíos.',
      },
      shipment_cancelled: {
        label: 'Cancelar envío',
        description: 'Se activa cuando se cancela manualmente un envío.',
      },
    },
  },
  returnsPortal: {
    label: 'Portal devoluciones',
    icon: MagnifyingGlassIcon,
    events: {
      order_recovery_link_requested: {
        label: 'Enlace de recuperación de pedido solicitado',
        description:
          'Se activa cuando un cliente busca su pedido por email en el portal de devoluciones y solicita el enlace para acceder a él.',
      },
    },
  },
  errors: {
    label: 'Errores',
    icon: WarningCircleIcon,
    events: {
      alert_raised: {
        label: 'Alerta registrada',
        description:
          'Se activa la primera vez que se dispara una alerta de cualquiera de los tipos seleccionados para una transacción o envío',
        valueOptions: {
          label: 'Alerta registrada',
          sections: buildAlertRaisedSections(),
        },
      },
      return_label_failed: {
        label: 'Error al crear etiqueta de devolución',
        description: 'Se activa cuando un error impide la creación de una etiqueta de devolución.',
      },
      exchange_order_error: {
        label: 'Error al crear pedido de cambio',
        description:
          'Se activa cuando un error impide la creación de un pedido de cambio, lo que provoca la cancelación de la solicitud.',
      },
    },
  },
})

export const getSections = t => {
  const sections = getTriggerEventSections()
  return Object.entries(sections).map(([key, section]) => ({ label: t(section.label), id: key }))
}

export const getMenuItems = (t, disabledTriggers = []) => {
  const sections = getTriggerEventSections()

  return Object.entries(sections).flatMap(([sectionKey, section]) => {
    return Object.entries(section.events).map(([eventKey, event]) => {
      const disabled = disabledTriggers.includes(eventKey)
      const label = t(event.label)

      return {
        label: disabled ? <DisabledTriggerLabel label={label} /> : label,
        value: eventKey,
        section: sectionKey,
        disabled,
      }
    })
  })
}

export const getValueMenuItems = (eventKey, t) => {
  const eventConfig = findEventConfig(eventKey)

  if (!eventConfig?.valueOptions) return null

  if (eventConfig.valueOptions.sections) {
    return Object.entries(eventConfig.valueOptions.sections).flatMap(([sectionKey, section]) => {
      return section.values.map(item => ({ ...item, label: t(item.label), section: sectionKey }))
    })
  }

  return eventConfig.valueOptions.values.map(item => ({ ...item, label: t(item.label) }))
}

export const getValueSections = (eventKey, t) => {
  const eventConfig = findEventConfig(eventKey)

  if (!eventConfig?.valueOptions?.sections) return null

  return Object.entries(eventConfig.valueOptions.sections).map(([key, section]) => ({
    label: t(section.label),
    id: key,
  }))
}

export const findEventConfig = eventKey => {
  if (!eventKey) return null
  const sections = getTriggerEventSections()
  for (const section of Object.values(sections)) {
    if (section.events[eventKey]) return section.events[eventKey]
  }
  return null
}
